import { TUNING } from '@cloudpearl/config';
import type { DeathCause, World } from '@cloudpearl/core';
import type { PearlState } from '../player/PlayerView';

/** One network snapshot of a pearl (sent ~20×/s during a shared run). */
export interface NetState {
  k: 's';
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** grounded / holding / alive / started as 0|1 */
  g: number;
  h: number;
  a: number;
  r: number;
  /** distance (score), storm front, combo flow */
  d: number;
  s: number;
  f: number;
  c: DeathCause | null;
}

export interface Predicted {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
}

/**
 * The friend's pearl in a shared run, shaped like a `Sim` for `PlayerView`. Snapshots are dead-
 * reckoned forward by their age plus half the round trip (so it shows where they are *now*), and
 * the shown position eases toward that prediction — big errors snap instead of sliding.
 */
export class RemotePearl implements PearlState {
  readonly world: World;
  started = false;
  alive = true;
  cause: DeathCause | null = null;
  grounded = true;
  holding = false;
  x = 0;
  y = 0;
  z = 0;
  prevX = 0;
  prevY = 0;
  prevZ = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  nx = 0;
  ny = 1;
  nz = 0;
  flow = 0;
  /** Their score and storm front, as last reported. */
  distance = 0;
  stormP = 0;
  /** A snapshot has arrived since the last reset. */
  live = false;
  private snap: NetState | null = null;
  private at = 0;
  private readonly p: Predicted = { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0 };

  constructor(world: World) {
    this.world = world;
  }

  get speed(): number {
    return Math.sqrt(this.vx * this.vx + this.vy * this.vy + this.vz * this.vz);
  }

  /** Waiting at the start line (menu / countdown). */
  park(x: number, z: number): void {
    this.snap = null;
    this.live = false;
    this.started = false;
    this.alive = true;
    this.cause = null;
    this.grounded = true;
    this.holding = false;
    this.x = this.prevX = x;
    this.z = this.prevZ = z;
    this.y = this.prevY = this.world.height(x, z);
    this.vx = this.vy = this.vz = 0;
    this.distance = 0;
  }

  receive(s: NetState, now: number): void {
    this.snap = s;
    this.at = now;
    this.live = true;
    this.distance = s.d;
    this.stormP = s.s;
  }

  /** Where the friend's pearl should be `ahead` seconds after `now` (dead reckoning). */
  predict(now: number, latency: number, ahead = 0): Predicted | null {
    const s = this.snap;
    if (!s) return null;
    // Snapshot age on the performance.now() clock, plus the trip here; never extrapolate far.
    const t = Math.max(0, Math.min(0.5, (now - this.at) / 1000 + latency + ahead));
    const p = this.p;
    p.vx = s.vx;
    p.vz = s.vz;
    p.x = s.x + s.vx * t;
    p.z = s.z + s.vz * t;
    if (s.g && s.a) {
      p.y = this.world.height(p.x, p.z);
      p.vy = s.vy;
    } else {
      const g = TUNING.gravity * (s.h ? TUNING.holdGravityMul : 1);
      p.vy = s.vy - g * t;
      p.y = s.y + s.vy * t - 0.5 * g * t * t;
      if (s.a && this.world.mask(p.x, p.z) > 0.5) p.y = Math.max(p.y, this.world.height(p.x, p.z));
    }
    return p;
  }

  /** Per render frame: ease the shown pearl toward the prediction. */
  update(now: number, latency: number, dt: number): void {
    const s = this.snap;
    if (!s) return;
    this.started = !!s.r;
    this.holding = !!s.h;
    this.flow = s.f;
    this.cause = s.c;
    const wasAlive = this.alive;
    this.alive = !!s.a;
    this.prevX = this.x;
    this.prevY = this.y;
    this.prevZ = this.z;
    const p = this.predict(now, latency)!;
    const err = Math.hypot(p.x - this.x, p.z - this.z);
    // Respawns and long stalls teleport; small corrections glide.
    const k = err > 10 || (!wasAlive && this.alive) ? 1 : 1 - Math.exp(-dt * 14);
    this.x += (p.x - this.x) * k;
    this.z += (p.z - this.z) * k;
    this.y += (p.y - this.y) * k;
    this.vx = p.vx;
    this.vy = p.vy;
    this.vz = p.vz;
    this.grounded = !!s.g;
    if (this.grounded) {
      this.y = this.world.height(this.x, this.z);
      const e = 0.5;
      const hx = (this.world.height(this.x + e, this.z) - this.world.height(this.x - e, this.z)) / (2 * e);
      const hz = (this.world.height(this.x, this.z + e) - this.world.height(this.x, this.z - e)) / (2 * e);
      const inv = 1 / Math.sqrt(hx * hx + 1 + hz * hz);
      this.nx = -hx * inv;
      this.ny = inv;
      this.nz = -hz * inv;
    }
  }
}
